import { useLocalStore, useObserver } from "mobx-react";
import React from "react";
import {
  useUpdateBookingProjectMutation,
  ProjectFragment,
  BookingProfileFragment
} from "@graphql";
import locale from "@bonliva-crm/locale";
import { store } from "@store";
import { OkCancelDialogOptions } from "@containers/ConfirmDialog";
import { ProjectSelectorStore } from "../../ProjectSelector";
import { Spinner, SpinnerOverlay } from "@components";
import { ProjectCard } from "@containers/ProjectCard";

type Props = {
  bookingId: string;
  data: BookingProfileFragment;
  clearable: boolean;
};

export function BookingProject(props: Props) {
  const { data, bookingId } = props;

  const [update] = useUpdateBookingProjectMutation();

  const localStore = useLocalStore(
    (source: Props) => ({
      projectSelectorStore: new ProjectSelectorStore(() => null),

      isUpdating: false,
      isSelecting: !data.Project,
      selectedProjectId: "",

      confirmUpdate(project: ProjectFragment) {
        if (!project) {
          this.isSelecting = true;
          return;
        }

        store()
          .ui.confirmDialog.confirm(this.getDialogOptions(project))
          .then((result) => {
            if (result === "ok") {
              this.update(project._id);
              return;
            }

            this.reset();
          });
      },

      update(projectId: string) {
        this.isUpdating = true;

        update({
          variables: {
            bookingId,
            projectId
          }
        }).finally(this.reset);
      },

      getDialogOptions(project: ProjectFragment) {
        const dialogOptions = new OkCancelDialogOptions();
        dialogOptions.title = locale.ChangeProjectConfirm;
        dialogOptions.message = locale.SelectedProjectIs(
          `${project.ProjectNumber} ${project.Name}`
        );

        return dialogOptions;
      },

      reset() {
        this.isUpdating = false;
        this.isSelecting = false;
      }
    }),
    props
  );

  return useObserver(() => {
    if (!data) {
      return <Spinner />;
    }

    return (
      <div style={{ position: "relative" }}>
        <SpinnerOverlay
          show={localStore.isUpdating}
          size={16}
          style={{ borderRadius: 4 }}
        />

        <ProjectCard
          data={data.Project}
          onClear={() => null}
          withClear={false}
          clearIcon={null}
          costCenter={data.CostCenter?.Name ?? ""}
        />
      </div>
    );
  });
}
